/* eslint-disable react/require-default-props */
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { FlexColumn, FlexRow } from "../GridsAndLayouts";
import Button from "../Button";
import Typography from "../Typography";
import {
  CollapseAltIcon,
  ExpandAltIcon,
  PauseIcon,
  PlayIcon,
  VolumeIcon,
  VolumeMuteIcon,
} from "../Icons";
import { useMaximize } from "../Maximize";
import { formatVideoTime } from "./formatVideoTime";
import {
  getYouTubeThumbnailUrl,
  parseYouTubeVideoId,
} from "./parseYouTubeVideoId";
import { useYouTubeEmbed } from "./useYouTubeEmbed";
import "./VideoPlayer.scss";

const previewShape = PropTypes.shape({
  tag: PropTypes.node,
  heading: PropTypes.node,
  body: PropTypes.node,
  segmentCount: PropTypes.number,
  activeSegment: PropTypes.number,
});

function PreviewSegments({ segmentCount, activeSegment = 0 }) {
  if (!segmentCount || segmentCount <= 1) {
    return null;
  }

  return (
    <div className="ds-video-player__segments" aria-hidden>
      {Array.from({ length: segmentCount }, (_, index) => (
        <span
          key={index}
          className={classNames("ds-video-player__segment", {
            "ds-video-player__segment--active": index === activeSegment,
          })}
        />
      ))}
    </div>
  );
}

PreviewSegments.propTypes = {
  segmentCount: PropTypes.number,
  activeSegment: PropTypes.number,
};

function PlayFab({ playLabel, onClick }) {
  return (
    <Button
      type="button"
      variant="secondary"
      size="l"
      className="ds-video-player__play-fab"
      aria-label={`Play ${playLabel}`}
      icon={
        <PlayIcon
          size={32}
          fill="var(--ds-theme-action-primary)"
          aria-hidden
        />
      }
      onClick={onClick}
    />
  );
}

PlayFab.propTypes = {
  playLabel: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

/**
 * Guided video player — preview overlay, custom controls, optional no-skip-ahead.
 * Pair with {@link useVideoPlayer} or pass controlled playback props.
 */
const VideoPlayer = ({
  src,
  poster,
  preview,
  previewVariant = "video",
  logoSrc,
  logoAlt = "",
  ariaLabel,
  resetKey,
  currentTime = 0,
  maxWatched = 0,
  playing = false,
  duration = 0,
  complete = false,
  setPlaying,
  onDurationChange,
  onTimeUpdate,
  onTogglePlayback,
  onSeek,
  onEnded,
  formatTime = formatVideoTime,
  showControls = true,
  mutedNote,
  allowExpand = true,
  footer,
  className = "",
}) => {
  const videoRef = useRef(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [muted, setMuted] = useState(false);
  // Frozen when playback starts so later timeupdates do not seek back here.
  const startAtRef = useRef(0);
  const resumeLandedRef = useRef(true);
  if (!hasStarted) {
    startAtRef.current = currentTime;
    resumeLandedRef.current = currentTime <= 0.25;
  }
  const {
    expanded,
    setExpanded,
    toggleExpanded,
    backdrop,
  } = useMaximize({ minimizeLabel: "Minimize video" });
  const setPlayingRef = useRef(setPlaying);
  setPlayingRef.current = setPlaying;
  const youtubeVideoId = parseYouTubeVideoId(src);
  const isYouTube = Boolean(youtubeVideoId);
  const youtubeThumbnail = getYouTubeThumbnailUrl(youtubeVideoId);

  const { containerRef: youtubeContainerRef, seekTo: seekYouTube } = useYouTubeEmbed({
    videoId: youtubeVideoId,
    resetKey,
    hasStarted: isYouTube && hasStarted,
    playing,
    muted,
    setPlaying,
    onDurationChange,
    onTimeUpdate,
    onEnded,
    startAtRef,
  });

  const showCardPreview =
    previewVariant === "card" && Boolean(preview) && !hasStarted;
  const showIdleChrome = !hasStarted && !showCardPreview;

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  const maxWatchedPercent = duration > 0 ? (maxWatched / duration) * 100 : 0;
  const playLabel =
    ariaLabel || (preview?.heading ? String(preview.heading) : "Video");

  const seekToResume = (video) => {
    const resumeAt = startAtRef.current;
    if (!video || complete || resumeAt <= 0.25 || video.readyState < 1) {
      return;
    }
    if (Math.abs(video.currentTime - resumeAt) <= 0.35) {
      return;
    }
    video.currentTime = resumeAt;
  };

  // `playing` is owned by the parent and reset in a parent effect, which runs
  // after this one — clear it here too so a new video never inherits the
  // previous step's playing state.
  useEffect(() => {
    setHasStarted(false);
    setMuted(false);
    setExpanded(false);
    setPlayingRef.current?.(false);
  }, [resetKey, src, setExpanded]);

  const pendingResumeAt = hasStarted ? null : currentTime;

  useEffect(() => {
    if (isYouTube || pendingResumeAt == null) {
      return undefined;
    }

    const video = videoRef.current;
    if (!video) {
      return undefined;
    }

    if (video.readyState >= 1) {
      seekToResume(video);
      return undefined;
    }

    const apply = () => seekToResume(video);
    video.addEventListener("loadedmetadata", apply);
    return () => video.removeEventListener("loadedmetadata", apply);
  }, [complete, isYouTube, pendingResumeAt, src]);

  useEffect(() => {
    if (isYouTube) {
      return undefined;
    }

    const video = videoRef.current;
    if (!video) {
      return undefined;
    }

    if (!(playing && hasStarted)) {
      video.pause();
      return undefined;
    }

    let cancelled = false;
    const start = () => {
      if (cancelled) {
        return;
      }
      seekToResume(video);
      video.play().catch(() => setPlaying?.(false));
    };

    if (video.readyState >= 1) {
      start();
      return undefined;
    }

    video.addEventListener("loadedmetadata", start);
    return () => {
      cancelled = true;
      video.removeEventListener("loadedmetadata", start);
    };
  }, [hasStarted, isYouTube, playing, setPlaying]);

  useEffect(() => {
    if (isYouTube) {
      return;
    }

    const video = videoRef.current;
    if (video) {
      video.muted = muted;
    }
  }, [isYouTube, muted]);

  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (video?.duration) {
      onDurationChange?.(video.duration);
    }
  };

  const handleTimeUpdate = () => {
    if (!hasStarted) {
      return;
    }

    const video = videoRef.current;
    if (!video) {
      return;
    }

    const resumeAt = startAtRef.current;
    // The element can report 0 before a resume seek lands.
    if (
      !resumeLandedRef.current
      && resumeAt > 0.25
      && video.currentTime + 0.75 < resumeAt
    ) {
      seekToResume(video);
      return;
    }

    resumeLandedRef.current = true;
    onTimeUpdate?.(video.currentTime);
  };

  const handleSeek = (event) => {
    const nextTime = Number(event.target.value);
    const allowedTime = onSeek?.(nextTime);

    if (allowedTime == null) {
      return;
    }

    if (isYouTube) {
      seekYouTube(allowedTime);
      return;
    }

    const video = videoRef.current;
    if (video) {
      video.currentTime = allowedTime;
    }
  };

  const startPlayback = () => {
    if (complete) {
      return;
    }
    setHasStarted(true);
    setPlaying?.(true);
  };

  const handleTogglePlayback = () => {
    if (!hasStarted) {
      startPlayback();
      return;
    }
    onTogglePlayback?.();
  };

  const handleToggleMute = () => {
    setMuted((value) => !value);
  };


  return (
    <FlexColumn
      className={classNames(
        "ds-video-player",
        { "ds-video-player--expanded": expanded },
        className,
      )}
      align="stretch"
      fullWidth
      gap="var(--ds-space-3)"
    >
      {/* Expanding only restyles this node — the media element is never
          remounted, so playback and watch progress are untouched. */}
      {backdrop}
      <FlexColumn align="stretch" fullWidth className="ds-video-player__frame">
        {isYouTube ? (
          <div
            className={classNames("ds-video-player__youtube-shell", {
              "ds-video-player__youtube-shell--started": hasStarted,
            })}
            style={
              !hasStarted && youtubeThumbnail
                ? { backgroundImage: `url("${youtubeThumbnail}")` }
                : undefined
            }
          >
            <div
              ref={youtubeContainerRef}
              className="ds-video-player__media ds-video-player__media--youtube"
            />
          </div>
        ) : (
          <video
            ref={videoRef}
            className="ds-video-player__media"
            src={src}
            poster={poster}
            preload="auto"
            playsInline
            muted={muted}
            onLoadedMetadata={handleLoadedMetadata}
            onTimeUpdate={handleTimeUpdate}
            onEnded={onEnded}
            onPlay={() => hasStarted && setPlaying?.(true)}
            onPause={() => hasStarted && setPlaying?.(false)}
          />
        )}

        {showCardPreview ? (
          <FlexColumn
            className="ds-video-player__preview"
            align="start"
            justify="center"
            fullWidth
            gap="var(--ds-space-3)"
            role="group"
            aria-label={`Video preview: ${playLabel}`}
          >
            {logoSrc ? (
              <img
                src={logoSrc}
                alt={logoAlt}
                className="ds-video-player__preview-logo"
              />
            ) : null}
            <FlexColumn
              align="start"
              fullWidth
              gap="var(--ds-space-3)"
              className="ds-video-player__preview-content"
            >
              {preview.tag != null && preview.tag !== "" ? (
                <Typography
                  component="span"
                  variant="bodyExtraSmall"
                  className="ds-video-player__preview-tag"
                >
                  {preview.tag}
                </Typography>
              ) : null}
              {preview.heading != null && preview.heading !== "" ? (
                <Typography
                  component="p"
                  variant="headingMedium"
                  fontWeight="semibold"
                  className="ds-video-player__preview-heading"
                >
                  {preview.heading}
                </Typography>
              ) : null}
              {preview.body != null && preview.body !== "" ? (
                <Typography
                  component="p"
                  variant="bodyMedium"
                  className="ds-video-player__preview-body"
                >
                  {preview.body}
                </Typography>
              ) : null}
            </FlexColumn>
            <PreviewSegments
              segmentCount={preview.segmentCount}
              activeSegment={preview.activeSegment ?? 0}
            />
            <PlayFab playLabel={playLabel} onClick={startPlayback} />
          </FlexColumn>
        ) : null}

        {showIdleChrome ? (
          <>
            {logoSrc ? (
              <img
                src={logoSrc}
                alt={logoAlt}
                className="ds-video-player__logo"
              />
            ) : null}
            {preview?.segmentCount ? (
              <PreviewSegments
                segmentCount={preview.segmentCount}
                activeSegment={preview.activeSegment ?? 0}
              />
            ) : null}
            <PlayFab playLabel={playLabel} onClick={startPlayback} />
          </>
        ) : null}
      </FlexColumn>

      {showControls ? (
        <FlexRow
          align="center"
          fullWidth
          gap="var(--ds-space-3)"
          className="ds-video-player__controls"
        >
          <Button
            type="button"
            variant="circular-primary"
            size="base"
            className="ds-video-player__play-btn"
            aria-label={playing ? "Pause" : "Play"}
            icon={
              playing ? (
                <PauseIcon size={16} aria-hidden />
              ) : (
                <PlayIcon size={16} aria-hidden />
              )
            }
            onClick={handleTogglePlayback}
          />
          <Button
            type="button"
            variant="tertiary"
            size="base"
            className="ds-video-player__icon-btn"
            aria-label={muted ? "Unmute" : "Mute"}
            aria-pressed={muted}
            icon={
              muted ? (
                <VolumeMuteIcon size={16} aria-hidden />
              ) : (
                <VolumeIcon size={16} aria-hidden />
              )
            }
            onClick={handleToggleMute}
          />
          <FlexRow
            align="center"
            fullWidth
            gap="var(--ds-space-3)"
            className="ds-video-player__scrubber-wrap"
          >
            <input
              type="range"
              min={0}
              max={duration || 0}
              step={0.1}
              value={currentTime}
              className="ds-video-player__scrubber"
              disabled={!hasStarted || duration <= 0}
              style={{
                background: `linear-gradient(to right, var(--ds-theme-action-primary) 0%, var(--ds-theme-action-primary) ${progressPercent}%, var(--ds-color-gray-100) ${progressPercent}%, var(--ds-color-gray-100) ${maxWatchedPercent}%, var(--pal-grey-white-300) ${maxWatchedPercent}%, var(--pal-grey-white-300) 100%)`,
              }}
              onChange={handleSeek}
              aria-label="Video progress"
            />
            <Typography
              component="span"
              variant="bodySmall"
              color="ui-subtle"
              className="ds-video-player__time"
            >
              {formatTime(currentTime)} / {formatTime(duration)}
            </Typography>
            {muted && mutedNote ? (
              <Typography
                component="span"
                variant="bodySmall"
                color="ui-subtle"
                className="ds-video-player__muted-note"
              >
                {mutedNote}
              </Typography>
            ) : null}
          </FlexRow>
          {allowExpand ? (
            <Button
              type="button"
              variant="tertiary"
              size="base"
              className="ds-video-player__icon-btn"
              aria-label={expanded ? "Minimize video" : "Maximize video"}
              aria-pressed={expanded}
              icon={
                expanded ? (
                  <CollapseAltIcon size={16} aria-hidden />
                ) : (
                  <ExpandAltIcon size={16} aria-hidden />
                )
              }
              onClick={toggleExpanded}
            />
          ) : null}
        </FlexRow>
      ) : null}

      {footer ? (
        <FlexColumn
          align="stretch"
          fullWidth
          className="ds-video-player__footer"
        >
          {footer}
        </FlexColumn>
      ) : null}
    </FlexColumn>
  );
};

VideoPlayer.propTypes = {
  src: PropTypes.string.isRequired,
  poster: PropTypes.string,
  preview: previewShape,
  /** `video` (default) — first frame + play button. `card` — purple preview card with copy. */
  previewVariant: PropTypes.oneOf(["video", "card"]),
  logoSrc: PropTypes.string,
  logoAlt: PropTypes.string,
  ariaLabel: PropTypes.string,
  resetKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentTime: PropTypes.number,
  maxWatched: PropTypes.number,
  playing: PropTypes.bool,
  duration: PropTypes.number,
  complete: PropTypes.bool,
  setPlaying: PropTypes.func,
  onDurationChange: PropTypes.func,
  onTimeUpdate: PropTypes.func,
  onTogglePlayback: PropTypes.func,
  onSeek: PropTypes.func,
  onEnded: PropTypes.func,
  formatTime: PropTypes.func,
  /** When false, hides the play/scrubber/time bar below the video. Default true. */
  showControls: PropTypes.bool,
  /** Shown beside the time when muted; does not change watch-to-completion. */
  mutedNote: PropTypes.node,
  /** When false, hides the maximize / minimize control. Default true. */
  allowExpand: PropTypes.bool,
  /** Rendered below the controls, and inside the card while maximized. */
  footer: PropTypes.node,
  className: PropTypes.string,
};

export { VideoPlayer };
export default VideoPlayer;
